import React, { useCallback, useState, useMemo } from "react";
import {
  View,
  ScrollView,
  ActivityIndicator,
  RefreshControl,
  TouchableOpacity,
  TextInput,
  StyleSheet,
} from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";
import Header from "../components/Header";
import ProgressBar from "../components/ProgressBar";
import DbCrateAccordion from "../components/DbCrateAccordion";
import MarkingSheet from "../components/MarkingSheet";
import UnpackConfirmSheet from "../components/UnpackConfirmSheet";
import CrateScannerSheet from "@components/ds/CrateScannerSheet";
import useRoutingData from "../hooks/useRoutingData";
import useCrateSearch from "../hooks/useCrateSearch";
import usePackUnpack from "../hooks/usePackUnpack";
import useMarking from "../hooks/useMarking";
import useDbInventory from "../hooks/useDbInventory";
import InventorySection from "../components/InventorySection";
import InventoryMarkSheet from "../components/InventoryMarkSheet";
import { tokens, sharedStyles } from "../styles";

// Per-DB packing detail screen — prototype-style. On entry we derive every
// crate assigned to this DB from the /get-assigned-dbs payload (same source
// the routing screen uses) and render them grouped by crate_size:
//
//   SMALL CRATES   → small + medium crates
//   LARGE CRATES   → large crates
//   SHIPMENT       → crates flagged as shipment / inventory / top-sku
//
// Each crate is a collapsible DbCrateAccordion that lazy-fetches its items
// via /get-crate-cx-items only on first expand. Header right has a search
// box that filters by crate id and a Scan button that opens the camera.

// Pure third-party shipments (is_shipment === true) are filtered out of
// dbCrates / connectingCrates before this classifier runs — they're
// handled by the delivery app, not the partner — so we only need to
// distinguish large from small here.
const sectionForCrate = (crate) => {
  const size = String(crate?.crate_size || "").toUpperCase();
  if (size === "LARGE") return "LARGE";
  return "SMALL";
};

const SECTION_LABELS = {
  SMALL: "SMALL CRATES",
  LARGE: "LARGE CRATES",
};

const PackingInterfaceScreen = ({ navigation, route }) => {
  const { db_name, cp_db_id } = route?.params || {};

  const { data, loading, reload } = useRoutingData();
  const { unpack, unpacking } = usePackUnpack({ reload });
  const {
    submitMarking,
    removeMarking,
    submitting: marking,
  } = useMarking({
    reload,
  });

  // Inventory (TOP_10_SKUS) is per (db_id, route_id). The DB this screen
  // belongs to may have multiple routes, but to keep MVP simple we pick
  // the first route from the DB's routes[] (matches the assignment shape
  // where each DB usually subscribes to one locality at a time). Multi-
  // route inventory rendering is a follow-up.
  const myDb = useMemo(() => {
    const cpDbs = Array.isArray(data?.cp_dbs) ? data.cp_dbs : [];
    return cpDbs.find((d) => String(d?.cp_db_id) === String(cp_db_id)) || null;
  }, [data, cp_db_id]);
  const inventoryRouteId = useMemo(() => {
    const routes = Array.isArray(myDb?.routes) ? myDb.routes : [];
    return routes[0]?.route_id ? String(routes[0].route_id) : null;
  }, [myDb]);
  const {
    skus: inventorySkus,
    loading: inventoryLoading,
    submitting: inventorySubmitting,
    packVisible: packInventoryVisible,
    markSku: markInventorySku,
  } = useDbInventory({
    db_id: cp_db_id,
    route_id: inventoryRouteId,
  });
  const [inventoryMarkTarget, setInventoryMarkTarget] = useState(null);

  const [tab, setTab] = useState("today");

  // Crates assigned to this DB — anyone in tp_list referencing cp_db_id.
  // Filter by Today/Rescheduled tab the same way the web does on the
  // Customer Crate Items page. is_shipment entries (pure third-party
  // shipments handled by the delivery app) are excluded — the partner
  // has no Pack Visible work for them.
  const dbCrates = useMemo(() => {
    const all = Array.isArray(data?.data?.CRATE) ? data.data.CRATE : [];
    const wantRescheduled = tab === "rescheduled";
    return all
      .filter((c) => !c?.is_shipment)
      .filter((c) =>
        Array.isArray(c?.tp_list)
          ? c.tp_list.some((tp) => String(tp?.cp_db_id) === String(cp_db_id))
          : false,
      )
      .filter((c) => Boolean(c?.is_rescheduled) === wantRescheduled);
  }, [data, cp_db_id, tab]);

  // Connecting crates — crates referenced by my dbCrates' connecting_*
  // arrays that I DON'T already see in dbCrates. These hold items for
  // customers who share crates with mine but whose other crate is on a
  // different DB. Read-only here; the executive can't pack them (only the
  // owning DB can), but seeing them surfaces cross-DB linkage so packing
  // doesn't happen blind.
  const connectingCrates = useMemo(() => {
    const all = Array.isArray(data?.data?.CRATE) ? data.data.CRATE : [];
    if (all.length === 0 || dbCrates.length === 0) return [];
    const myCrateIds = new Set(dbCrates.map((c) => String(c?.crate_id)));
    const connectingIds = new Set();
    dbCrates.forEach((c) => {
      (Array.isArray(c?.connecting_small_crates)
        ? c.connecting_small_crates
        : []
      ).forEach((id) => {
        if (id && !myCrateIds.has(String(id))) connectingIds.add(String(id));
      });
      (Array.isArray(c?.connecting_large_crates)
        ? c.connecting_large_crates
        : []
      ).forEach((id) => {
        if (id && !myCrateIds.has(String(id))) connectingIds.add(String(id));
      });
    });
    if (connectingIds.size === 0) return [];
    const wantRescheduled = tab === "rescheduled";
    return all.filter(
      (c) =>
        !c?.is_shipment &&
        connectingIds.has(String(c?.crate_id)) &&
        Boolean(c?.is_rescheduled) === wantRescheduled,
    );
  }, [data, dbCrates, tab]);

  const [searchQuery, setSearchQuery] = useState("");
  const handleAutoSelect = useCallback(() => {
    // No-op: search results just filter the visible list rather than
    // navigating away.
  }, []);
  const { search } = useCrateSearch({
    tab,
    onAutoSelect: handleAutoSelect,
  });

  const handleSearchChange = (text) => {
    setSearchQuery(text);
    search(text);
  };

  // Filter crates by search query (substring match on crate_id / title).
  const visibleCrates = useMemo(() => {
    if (!searchQuery) return dbCrates;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return dbCrates;
    return dbCrates.filter(
      (c) =>
        String(c?.crate_id || "")
          .toLowerCase()
          .includes(q) ||
        String(c?.title || "")
          .toLowerCase()
          .includes(q),
    );
  }, [dbCrates, searchQuery]);

  const grouped = useMemo(() => {
    const map = { SMALL: [], LARGE: [], SHIPMENT: [] };
    visibleCrates.forEach((c) => {
      map[sectionForCrate(c)].push(c);
    });
    return map;
  }, [visibleCrates]);

  // Headline progress: unique customers fully packed / total assigned customers.
  const { totalCxs, packedCxs, totalItems, packedItems } = useMemo(() => {
    let tCx = 0;
    let pCx = 0;
    let tIt = 0;
    let pIt = 0;
    dbCrates.forEach((c) => {
      const tps = Array.isArray(c?.tp_list)
        ? c.tp_list.filter((tp) => String(tp?.cp_db_id) === String(cp_db_id))
        : [];
      tCx += tps.length;
      const ratio =
        Number(c?.tp_count) > 0
          ? Number(c?.packed_tps || 0) / Number(c.tp_count)
          : 0;
      pCx += Math.round(tps.length * ratio);
      tIt += Number(c?.tp_count || 0);
      pIt += Number(c?.packed_tps || 0);
    });
    return {
      totalCxs: tCx,
      packedCxs: pCx,
      totalItems: tIt,
      packedItems: pIt,
    };
  }, [dbCrates, cp_db_id]);

  const overallPct =
    totalCxs > 0 ? Math.round((packedCxs / totalCxs) * 100) : 0;
  const issuesCount = useMemo(() => {
    // Best-effort: derive from unassigned_tp_list length if surfaced; else 0.
    return dbCrates.reduce(
      (s, c) => s + Number(c?.unassigned_tp_list?.length || 0),
      0,
    );
  }, [dbCrates]);

  const [unpackTarget, setUnpackTarget] = useState(null);
  const [markTarget, setMarkTarget] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  const sectionsToRender = ["SMALL", "LARGE", "SHIPMENT"].filter(
    (k) => grouped[k] && grouped[k].length > 0,
  );

  const handleMarkSelected = useCallback(
    (item) => {
      if (!item) return;
      if (item.is_issue) {
        removeMarking(item);
        return;
      }
      setMarkTarget(item);
    },
    [removeMarking],
  );

  return (
    <View style={sharedStyles.screen}>
      <Header
        title={`Pack · ${db_name || "DB"}`}
        subtitle={`${dbCrates.length} crate${dbCrates.length === 1 ? "" : "s"} assigned`}
        onBack={() => navigation.goBack()}
        right={
          <TouchableOpacity
            activeOpacity={0.85}
            onPress={() => setScannerOpen(true)}
            style={styles.headerScanBtn}
          >
            <Text
              variant="caption_2"
              numberOfLines={1}
              style={styles.headerScanText}
            >
              Scan
            </Text>
          </TouchableOpacity>
        }
      />
      <View style={styles.tabBar}>
        {[
          { key: "today", label: "Today" },
          { key: "rescheduled", label: "Rescheduled" },
        ].map((t) => {
          const isActive = t.key === tab;
          return (
            <TouchableOpacity
              key={t.key}
              activeOpacity={0.85}
              onPress={() => setTab(t.key)}
              style={[styles.tabBtn, isActive && styles.tabBtnActive]}
            >
              <Text
                variant="caption_2"
                style={[
                  styles.tabBtnText,
                  isActive ? styles.tabBtnTextActive : styles.tabBtnTextIdle,
                ]}
              >
                {t.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
      <View style={styles.searchBar}>
        <TextInput
          value={searchQuery}
          onChangeText={handleSearchChange}
          placeholder="Search crate id…"
          placeholderTextColor={tokens.textTertiary}
          style={styles.searchInput}
          returnKeyType="search"
          testID="packing-search-input"
        />
      </View>
      {loading && !data ? (
        <View style={sharedStyles.centered}>
          <ActivityIndicator color={color.mediumBlack} />
        </View>
      ) : (
        <ScrollView
          contentContainerStyle={styles.scroll}
          refreshControl={
            <RefreshControl
              refreshing={loading}
              onRefresh={reload}
              tintColor={color.mediumBlack}
            />
          }
        >
          <View style={[sharedStyles.card, styles.summary]}>
            <View style={styles.summaryRow}>
              <Text variant="caption_2" style={styles.summaryLabel}>
                Packing Progress
              </Text>
              <Text variant="title_16" style={styles.summaryPct}>
                {overallPct}%
              </Text>
            </View>
            <ProgressBar percent={overallPct} height={10} />
            <View style={styles.summaryStatsRow}>
              <Text variant="caption_2" style={styles.summaryStat}>
                ✓ {packedCxs} packed
              </Text>
              <Text variant="caption_2" style={styles.summaryStat}>
                ◯ {Math.max(0, totalCxs - packedCxs)} unpacked
              </Text>
              {issuesCount > 0 ? (
                <Text variant="caption_2" style={styles.summaryStatIssue}>
                  ! {issuesCount} issues
                </Text>
              ) : null}
            </View>
            <Text variant="caption_2" style={styles.summaryFootnote}>
              {packedItems} / {totalItems} customers across {dbCrates.length}{" "}
              crates
            </Text>
          </View>

          <InventorySection
            skus={inventorySkus}
            loading={inventoryLoading}
            submitting={inventorySubmitting}
            onPackAll={packInventoryVisible}
            onMarkSku={(sku) => setInventoryMarkTarget(sku)}
          />
          {sectionsToRender.length === 0 ? (
            <Text variant="caption_2" style={sharedStyles.emptyText}>
              {searchQuery
                ? `No crates match "${searchQuery}".`
                : "No crates assigned to this DB yet."}
            </Text>
          ) : (
            sectionsToRender.map((key) => (
              <View key={key} style={styles.section}>
                <View style={styles.sectionHeaderRow}>
                  <View style={styles.sectionAccent} />
                  <Text variant="caption_2" style={styles.sectionHeader}>
                    {SECTION_LABELS[key]} ({grouped[key].length}{" "}
                    {grouped[key].length === 1 ? "CRATE" : "CRATES"})
                  </Text>
                </View>
                {grouped[key].map((crate) => (
                  <DbCrateAccordion
                    key={String(crate?.crate_id || crate?.title)}
                    summaryCrate={crate}
                    tab={tab}
                    onPressMark={handleMarkSelected}
                    setUnpackTarget={setUnpackTarget}
                    parentReload={reload}
                  />
                ))}
              </View>
            ))
          )}
          {connectingCrates.length > 0 ? (
            <View style={[styles.section, styles.connectingSection]}>
              <View style={styles.sectionHeaderRow}>
                <View
                  style={[styles.sectionAccent, styles.sectionAccentInfo]}
                />
                <Text
                  variant="caption_2"
                  style={[styles.sectionHeader, styles.sectionHeaderInfo]}
                >
                  CONNECTING CRATES ({connectingCrates.length})
                </Text>
              </View>
              <Text variant="caption_2" style={styles.connectingHint}>
                Read-only. Packed by the DB they&apos;re assigned to. Listed
                here because at least one customer has items both in your
                crate(s) and these.
              </Text>
              {connectingCrates.map((crate) => {
                const tps = Array.isArray(crate?.tp_list) ? crate.tp_list : [];
                const ownerNames = Array.from(
                  new Set(
                    tps
                      .map((tp) => tp?.db_name)
                      .filter((n) => n && typeof n === "string"),
                  ),
                );
                const ownerLabel = ownerNames.length
                  ? ownerNames.join(", ")
                  : "—";
                return (
                  <View
                    key={String(crate?.crate_id || crate?.title)}
                    style={[sharedStyles.card, styles.connectingCard]}
                  >
                    <Text
                      variant="title_16"
                      style={styles.connectingTitle}
                      numberOfLines={1}
                    >
                      {crate?.title || crate?.crate_id || "—"}
                    </Text>
                    <Text variant="caption_2" style={styles.connectingMeta}>
                      Locality: {crate?.dc || crate?.route_name || "—"}
                      {crate?.crate_size ? ` · ${crate.crate_size}` : ""}
                      {tps.length > 0
                        ? ` · ${tps.length} customer${tps.length === 1 ? "" : "s"}`
                        : ""}
                    </Text>
                    <Text variant="caption_2" style={styles.connectingOwner}>
                      Assigned to: {ownerLabel}
                    </Text>
                  </View>
                );
              })}
            </View>
          ) : null}
        </ScrollView>
      )}
      <UnpackConfirmSheet
        visible={Boolean(unpackTarget)}
        crate={unpackTarget}
        unpacking={unpacking}
        onClose={() => setUnpackTarget(null)}
        onConfirm={async () => {
          const ok = await unpack(unpackTarget);
          if (ok) setUnpackTarget(null);
        }}
      />
      <MarkingSheet
        visible={Boolean(markTarget)}
        item={markTarget}
        submitting={marking}
        onClose={() => setMarkTarget(null)}
        onSubmit={async (payload) => {
          const ok = await submitMarking(payload);
          if (ok) setMarkTarget(null);
        }}
      />
      <CrateScannerSheet
        visible={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDecode={(id) => {
          setScannerOpen(false);
          if (id) handleSearchChange(String(id));
        }}
      />
      <InventoryMarkSheet
        visible={Boolean(inventoryMarkTarget)}
        sku={inventoryMarkTarget}
        submitting={inventorySubmitting}
        onClose={() => setInventoryMarkTarget(null)}
        onSubmit={async (payload) => {
          const ok = await markInventorySku(payload);
          if (ok) setInventoryMarkTarget(null);
        }}
      />
    </View>
  );
};

export default PackingInterfaceScreen;

const styles = StyleSheet.create({
  scroll: {
    paddingTop: tokens.spacing.medium,
    paddingBottom: tokens.spacing.xlarge,
  },
  headerScanBtn: {
    paddingHorizontal: tokens.spacing.small,
    paddingVertical: 6,
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.primary.bg,
    minWidth: 56,
    alignItems: "center",
    justifyContent: "center",
  },
  headerScanText: {
    color: tokens.primary.fg,
    fontSize: 12,
    fontWeight: "700",
    textAlign: "center",
  },
  tabBar: {
    flexDirection: "row",
    backgroundColor: tokens.surface,
    paddingHorizontal: tokens.spacing.large,
    paddingTop: tokens.spacing.small,
    paddingBottom: 0,
  },
  tabBtn: {
    flex: 1,
    paddingVertical: 10,
    alignItems: "center",
    borderBottomWidth: 2,
    borderBottomColor: tokens.transparent,
  },
  tabBtnActive: {
    borderBottomColor: tokens.primary.bg,
  },
  tabBtnText: { fontSize: 13, fontWeight: "600" },
  tabBtnTextActive: { color: tokens.primary.bg, fontWeight: "700" },
  tabBtnTextIdle: { color: tokens.textSecondary },
  searchBar: {
    backgroundColor: tokens.surface,
    paddingHorizontal: tokens.spacing.large,
    paddingVertical: tokens.spacing.small,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: tokens.borderLight,
  },
  searchInput: {
    backgroundColor: tokens.surfaceHover,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: 8,
    fontSize: 14,
    color: tokens.textPrimary,
  },
  summary: { paddingVertical: tokens.spacing.large },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: tokens.spacing.small,
  },
  summaryLabel: {
    color: tokens.textSecondary,
    fontSize: 12,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  summaryPct: {
    color: tokens.textPrimary,
    fontWeight: "700",
    fontSize: 18,
  },
  summaryStatsRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    marginTop: tokens.spacing.medium,
  },
  summaryStat: {
    color: tokens.textSecondary,
    fontSize: 12,
    marginRight: tokens.spacing.medium,
  },
  summaryStatIssue: {
    color: tokens.warning,
    fontSize: 12,
    fontWeight: "700",
    marginRight: tokens.spacing.medium,
  },
  summaryFootnote: {
    color: tokens.textTertiary,
    fontSize: 11,
    marginTop: tokens.spacing.small,
  },
  section: { marginTop: tokens.spacing.medium },
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    marginHorizontal: tokens.spacing.page,
    marginBottom: tokens.spacing.small,
  },
  sectionAccent: {
    width: 4,
    height: 16,
    backgroundColor: tokens.primary.bg,
    marginRight: tokens.spacing.small,
    borderRadius: 2,
  },
  sectionHeader: {
    color: tokens.textSecondary,
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
  connectingSection: { marginTop: tokens.spacing.large },
  sectionAccentInfo: { backgroundColor: tokens.info },
  sectionHeaderInfo: { color: tokens.info },
  connectingHint: {
    color: tokens.textTertiary,
    fontSize: 11,
    marginHorizontal: tokens.spacing.page,
    marginBottom: tokens.spacing.small,
    fontStyle: "italic",
  },
  connectingCard: {
    marginHorizontal: tokens.spacing.page,
    marginBottom: tokens.spacing.small,
    paddingVertical: tokens.spacing.medium,
  },
  connectingTitle: {
    color: tokens.textPrimary,
    fontWeight: "700",
    fontSize: 14,
  },
  connectingMeta: {
    color: tokens.textSecondary,
    fontSize: 12,
    marginTop: 2,
  },
  connectingOwner: {
    color: tokens.info,
    fontSize: 11,
    marginTop: 4,
    fontWeight: "600",
  },
});
